import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/it'
import i18next from 'i18next'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)

export const formatDate = (date: string | Date, formatType = 'YYYY-MM-DD') =>
    date ? dayjs(date).format(formatType) : '-'

export const formatLocaleDate = (date: string | Date, formatType = 'YYYY-MM-DD') =>
    dayjs(date).locale(i18next.language).format(formatType)

export const formatDisplayDate = (date: Date): string => formatLocaleDate(date, 'DD/MM/YYYY')
export const formatDisplayTime = (date: Date | string): string => formatLocaleDate(date, 'HH:mm')
export const formatDisplayDateTime = (date: Date | string): string => formatLocaleDate(date, 'DD/MM/YYYY HH:mm')

export const getTimezone = (): string => {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    return dayjs.tz.guess()
}

export const createDateTime = (date: Date, time: string): Date => {
    return dayjs(`${formatDate(date)} ${time}`).toDate()
}

/**
 * Get the localized day name for a given day of week
 * @param dayOfWeek - The day of week (e.g. 'monday') to get the localized name for
 * @returns The localized day name
 */
export const getLocalizedDayName = (dayOfWeek: string): string => {
    // Map English day names to dayjs day numbers (0-6, where 0 is Sunday)
    const dayMap: Record<string, number> = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
    }

    const dayNumber = dayMap[dayOfWeek.toLowerCase()]
    if (dayNumber !== undefined) {
        // Create a date with the specific day of week and format it
        return formatLocaleDate(dayjs().day(dayNumber).toDate(), 'dddd')
    }

    return dayOfWeek // fallback to original value
}

export default dayjs

/**
 * Check if a date range represents a whole year (Jan 1 to Dec 31 of the same year)
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns true if the range represents a whole year, false otherwise
 */
export const isWholeYear = (startDate: string | Date, endDate: string | Date): boolean => {
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    return (
        start.month() === 0 &&
        start.date() === 1 &&
        end.month() === 11 &&
        end.date() === 31 &&
        start.year() === end.year()
    )
}

/**
 * Check if a date range represents a whole month or a whole year
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns true if the range represents a whole month or whole year, false otherwise
 */
export const isWholeMonthOrYear = (startDate: string | Date, endDate: string | Date): boolean => {
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    if (isWholeYear(startDate, endDate)) {
        return true
    }

    // Check if it's a whole month
    const isWholeMonth =
        start.date() === 1 &&
        end.date() === end.daysInMonth() &&
        start.month() === end.month() &&
        start.year() === end.year()

    return isWholeMonth
}
